/**
 * 지출 등록 — 불러오기(상위문서|현액) · 축 중복 경고
 */

import { hiCol } from 'components/grid/hiGrid';
import { getCachedBalances, filterBalances } from 'budget/budgetBalanceStore';
import { loadEnvSettings } from 'system/envSettingsStore';
import {
  EXPENSE_STATUS,
  expenseStatusLabel,
  loadObligationBundle,
  loadProposalBundle,
} from 'expense/expenseStore';

/** 현액 직등록 시 앞단(품의·원인행위) 존재 처리 */
export const UPSTREAM_DUP_MODE = {
  WARN: 'WARN',
  BLOCK: 'BLOCK',
  OFF: 'OFF',
};

export const UPSTREAM_DUP_MODE_OPTIONS = [
  { value: UPSTREAM_DUP_MODE.WARN, label: '경고 후 계속 (기본)' },
  { value: UPSTREAM_DUP_MODE.BLOCK, label: '차단' },
  { value: UPSTREAM_DUP_MODE.OFF, label: '검사 안 함' },
];

/** 불러오기 탭 표시 */
export const EXPENSE_LOAD_TABS = {
  BOTH: 'BOTH',
  UPSTREAM_ONLY: 'UPSTREAM_ONLY',
  BALANCE_ONLY: 'BALANCE_ONLY',
};

export const EXPENSE_LOAD_TABS_OPTIONS = [
  { value: EXPENSE_LOAD_TABS.BOTH, label: '상위문서 + 현액' },
  { value: EXPENSE_LOAD_TABS.UPSTREAM_ONLY, label: '상위문서만' },
  { value: EXPENSE_LOAD_TABS.BALANCE_ONLY, label: '현액만' },
];

export const EXPENSE_LOAD_DEFAULT_TAB = {
  UPSTREAM: 'UPSTREAM',
  BALANCE: 'BALANCE',
};

export const EXPENSE_LOAD_DEFAULT_TAB_OPTIONS = [
  { value: EXPENSE_LOAD_DEFAULT_TAB.UPSTREAM, label: '상위문서 우선' },
  { value: EXPENSE_LOAD_DEFAULT_TAB.BALANCE, label: '현액 우선' },
];

export function expenseAxisKey(row = {}) {
  return [
    String(row.deptCode || '').trim(),
    String(row.projectCode || '').trim(),
    String(row.subjectCode || row.subjectDispCode || '').trim(),
    String(row.fundCode || '').trim(),
  ].join('|');
}

export function sameExpenseAxis(a, b) {
  if (!a || !b) return false;
  return expenseAxisKey(a) === expenseAxisKey(b);
}

const LIVE_UPSTREAM = new Set([
  EXPENSE_STATUS.DRAFT,
  EXPENSE_STATUS.REQUESTED,
  EXPENSE_STATUS.APPROVED,
  EXPENSE_STATUS.CONFIRMED,
]);

/**
 * @returns {{ mode: string, resolutionTabs: string, resolutionDefaultTab: string, obligationTabs: string, obligationDefaultTab: string }}
 */
export function getExpenseLoadEnv(tenantCode) {
  const exp = loadEnvSettings(tenantCode)?.expense || {};
  const tabsOk = (v) =>
    v === EXPENSE_LOAD_TABS.UPSTREAM_ONLY ||
    v === EXPENSE_LOAD_TABS.BALANCE_ONLY ||
    v === EXPENSE_LOAD_TABS.BOTH
      ? v
      : EXPENSE_LOAD_TABS.BOTH;
  const defOk = (v) =>
    v === EXPENSE_LOAD_DEFAULT_TAB.BALANCE
      ? EXPENSE_LOAD_DEFAULT_TAB.BALANCE
      : EXPENSE_LOAD_DEFAULT_TAB.UPSTREAM;
  const mode =
    exp.upstreamDupMode === UPSTREAM_DUP_MODE.BLOCK || exp.upstreamDupMode === UPSTREAM_DUP_MODE.OFF
      ? exp.upstreamDupMode
      : UPSTREAM_DUP_MODE.WARN;
  return {
    mode,
    resolutionTabs: tabsOk(exp.resolutionLoadTabs),
    resolutionDefaultTab: defOk(exp.resolutionLoadDefaultTab),
    obligationTabs: tabsOk(exp.obligationLoadTabs),
    obligationDefaultTab: defOk(exp.obligationLoadDefaultTab),
  };
}

/** 동일 예산축의 유효 원인행위 */
export async function findObligationsOnAxis(dims, axisRow) {
  const key = expenseAxisKey(axisRow);
  if (!key || key === '|||') return [];
  const bundle = await loadObligationBundle(dims);
  return (bundle.masters || []).filter(
    (m) => m.mod !== 'D' && LIVE_UPSTREAM.has(m.status) && expenseAxisKey(m) === key
  );
}

/** 동일 예산축의 유효 품의 */
export async function findProposalsOnAxis(dims, axisRow) {
  const key = expenseAxisKey(axisRow);
  if (!key || key === '|||') return [];
  const bundle = await loadProposalBundle(dims);
  return (bundle.masters || []).filter(
    (m) => m.mod !== 'D' && LIVE_UPSTREAM.has(m.status) && expenseAxisKey(m) === key
  );
}

/**
 * 현액 선택 시 앞단 존재 확인. false면 적용 중단.
 * @param {'obligation'|'proposal'} upstreamKind - 결의→원인행위, 원인행위→품의
 */
export async function confirmUpstreamDupIfNeeded({
  tenantCode,
  dims,
  balanceRow,
  upstreamKind,
  showConfirm,
  showAlert,
}) {
  const { mode } = getExpenseLoadEnv(tenantCode);
  if (mode === UPSTREAM_DUP_MODE.OFF) return true;

  const hits =
    upstreamKind === 'proposal'
      ? await findProposalsOnAxis(dims, balanceRow)
      : await findObligationsOnAxis(dims, balanceRow);

  if (!hits.length) return true;

  const label = upstreamKind === 'proposal' ? '품의' : '원인행위';
  const sample = hits
    .slice(0, 3)
    .map((m) => {
      const no = m.obligationNo || m.proposalNo || '';
      return `${no}(${expenseStatusLabel(m.status)})`;
    })
    .join(', ');
  const more = hits.length > 3 ? ` 외 ${hits.length - 3}건` : '';
  const msg = `동일 예산축에 ${label}이(가) 이미 있습니다.\n${sample}${more}\n\n현액으로 직접 등록하면 이중 생성이 될 수 있습니다.`;

  if (mode === UPSTREAM_DUP_MODE.BLOCK) {
    showAlert?.(`${msg}\n\n환경설정에서 차단 모드입니다. 상위 문서를 불러오세요.`);
    return false;
  }
  return showConfirm?.(`${msg}\n\n그래도 현액으로 계속할까요?`);
}

export function balanceLookupColumns() {
  return [
    hiCol.roTextColumn({ header: '부서', field: 'deptName', size: 100 }),
    hiCol.roTextColumn({ header: '사업', field: 'projectName', size: 120 }),
    hiCol.roTextColumn({ header: '과목코드', field: 'subjectDispCode', size: 100 }),
    hiCol.roTextColumn({ header: '과목명', field: 'subjectName', size: 120 }),
    hiCol.roTextColumn({ header: '재원', field: 'fundName', size: 80 }),
    hiCol.roNumberColumn({ header: '현액', field: 'balanceAmt', size: 100 }),
    hiCol.roNumberColumn({ header: '가용', field: 'availableAmt', size: 100 }),
  ];
}

export function loadExpenseBalanceRows(tenantCode, fiscalYear) {
  const all = getCachedBalances({ tenantCode, fiscalYear });
  return filterBalances(all, { inoutDiv: '지출' }).map((r) => ({
    ...r,
    id: r.balanceUid || `${r.deptCode}-${r.projectCode}-${r.subjectCode}-${r.fundCode}`,
  }));
}

export const BALANCE_SEARCH_CONFIG = {
  mode: 'unified',
  keywordFields: [
    'deptCode',
    'deptName',
    'projectCode',
    'projectName',
    'subjectDispCode',
    'subjectCode',
    'subjectName',
    'fundCode',
    'fundName',
  ],
};

/**
 * 탭 배열 구성. tabsMode / defaultTab 반영.
 * @returns {{ tabs: array|null, defaultTabId: string, title: string }}
 */
export function buildLoadDialogTabs({
  tabsMode,
  defaultTab,
  upstreamTab,
  balanceRows,
  balanceOnlyTitle = '현액 조회 (지출)',
  bothTitle = '불러오기',
}) {
  const showUpstream =
    tabsMode === EXPENSE_LOAD_TABS.BOTH || tabsMode === EXPENSE_LOAD_TABS.UPSTREAM_ONLY;
  const showBalance =
    tabsMode === EXPENSE_LOAD_TABS.BOTH || tabsMode === EXPENSE_LOAD_TABS.BALANCE_ONLY;

  const balanceTab = {
    id: 'balance',
    label: '현액',
    hint: '현액만으로 등록합니다. 앞단 문서가 있으면 경고할 수 있습니다.',
    columns: balanceLookupColumns(),
    rows: balanceRows || [],
  };

  if (showUpstream && showBalance && upstreamTab) {
    const tabs =
      defaultTab === EXPENSE_LOAD_DEFAULT_TAB.BALANCE
        ? [balanceTab, upstreamTab]
        : [upstreamTab, balanceTab];
    return { tabs, defaultTabId: tabs[0].id, title: bothTitle };
  }
  if (showUpstream && upstreamTab) {
    return {
      tabs: null,
      defaultTabId: '',
      title: upstreamTab.label || '상위문서 조회',
      columns: upstreamTab.columns,
      rows: upstreamTab.rows,
      hint: upstreamTab.hint,
    };
  }
  return {
    tabs: null,
    defaultTabId: '',
    title: balanceOnlyTitle,
    columns: balanceTab.columns,
    rows: balanceTab.rows,
    hint: balanceTab.hint,
  };
}

/** 마스터 폼에 현액 축 반영 */
export function applyBalanceFields(setValue, row) {
  const r = Array.isArray(row) ? row[0] : row;
  if (!r || !setValue) return;
  setValue('balanceUid', r.balanceUid || '');
  setValue('deptCode', r.deptCode || '');
  setValue('deptName', r.deptName || '');
  setValue('projectCode', r.projectCode || '');
  setValue('projectName', r.projectName || '');
  setValue('subjectCode', r.subjectDispCode || r.subjectCode || '');
  setValue('subjectName', r.subjectName || '');
  setValue('fundCode', r.fundCode || '');
  setValue('fundName', r.fundName || '');
}
